// The custitem_cpq2configuration snapshot format — built on Save & Submit and
// read back by the Item Template / Edit Config shortcut. One source of truth so
// the format can evolve without breaking older stored data.
//
// ENCODING: JSON -> raw DEFLATE -> base64url, behind the "df1:" prefix.
//   - Self-contained & always correct: it's just a compressor over the exact
//     JSON of the state — no codebook, no dependency on questions.json or on
//     NetSuite's (fallible) custitem_mapping. JSON.stringify/parse round-trips
//     every value type losslessly, so decode can never silently mis-map a field.
//   - Dense: raw DEFLATE beats lz-string by ~33% on this data (measured 1772 vs
//     2642 chars for a 102-field config), which keeps large configs under the
//     ~4000-char field cap.
//   - Transport-safe: base64url output ([A-Za-z0-9-_], no padding) is 1 byte per
//     char and survives NetSuite storage + the REST/JSON round-trip.
//   - DEFLATE via the standard CompressionStream API (all modern browsers +
//     Node 18+). If it's ever unavailable, WRITES fall back to the lz-string
//     "lzc:" form so nothing breaks.
//
// COMPATIBILITY CONTRACT:
//   • Adding a field to the snapshot object is safe: write it in
//     buildConfigSnapshot and read it with a default in parseConfigSnapshot.
//   • parseConfigSnapshot reads every legacy prefix (df1:/lzc:/lz16:/bare-JSON),
//     so previously-stored items keep decoding. Deploy the reader before writing
//     a new format (an older build won't recognize df1: and would fall back to
//     the item mapping).
//   • Restructuring an existing field is BREAKING: bump CONFIG_SNAPSHOT_VERSION
//     and branch on `v` in parseConfigSnapshot.

import LZString from "lz-string";
import type { AnswerState, QTables, SelectedRowState, TableState } from "../types";

// The configurator every configuration saved BEFORE the product stamp existed was
// built with. A snapshot without `product` (v1, or the legacy bare-answers form)
// resolves to this, which is correct rather than a guess: Natural Draft Burners was
// the only configurator that had ever run.
//
// DEFINED HERE, not in data/loader.ts, on purpose. It is a fact about the snapshot
// format, and this module must stay free of the loader — loader.ts reads
// import.meta.env at module scope, so importing it dragged Vite-only globals into
// every Node script that touches a snapshot (test:snapshot died on exactly that).
// A hard constant, not "the first entry in products.json": reordering a list must
// never silently re-point legacy items at a different configurator.
export const LEGACY_PRODUCT_SLUG = "ndb";

// v2 (2026-08-11) added `product`. ADDITIVE, not breaking: a v1 snapshot has no
// product key and resolves to LEGACY_PRODUCT_SLUG, which is correct because Natural
// Draft Burners was the only configurator that had ever run. The bump exists so the
// two cases stay distinguishable — a v1 snapshot with no product is history, a v2
// snapshot with no product is a bug in the writer.
export const CONFIG_SNAPSHOT_VERSION = 2;

const DEFLATE_PREFIX = "df1:"; // JSON -> deflate-raw -> base64url (primary)
const COMPRESS_PREFIX = "lzc:"; // legacy: lz-string compressToEncodedURIComponent
const LEGACY_UTF16_PREFIX = "lz16:"; // legacy: lz-string UTF-16 (byte-cap hazard; read-only)

// The normalized, fully-defaulted shape the app restores from.
export type ConfigSnapshot = {
  v: number;
  // WHICH CONFIGURATOR BUILT THIS (a products.json slug, e.g. "ndb"). This is how
  // Edit Config knows which CPQ to open: the item carries the snapshot, the snapshot
  // names the configurator, so no NetSuite field and no change to the Sales Order
  // buttons is needed. Never blank on read — a v1 snapshot resolves to
  // LEGACY_PRODUCT_SLUG.
  product: string;
  answers: AnswerState;
  selectedRows: SelectedRowState;
  tables: TableState;
  // Extra inlet Aux columns the user added beyond the built-in Aux 1-3.
  extraAux: number;
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

// --- base64url <-> bytes (btoa/atob exist in browser and Node 18+) --------------
function bytesToB64url(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function b64urlToBytes(s: string): Uint8Array {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// --- raw DEFLATE via the standard CompressionStream API -------------------------
function hasCompression(): boolean {
  return typeof CompressionStream !== "undefined" && typeof DecompressionStream !== "undefined";
}
async function runStream(
  transform: { writable: WritableStream; readable: ReadableStream },
  bytes: Uint8Array
): Promise<Uint8Array> {
  const writer = transform.writable.getWriter();
  void writer.write(bytes);
  void writer.close();
  const reader = transform.readable.getReader();
  const chunks: Uint8Array[] = [];
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) chunks.push(value as Uint8Array);
  }
  let len = 0;
  for (const c of chunks) len += c.length;
  const out = new Uint8Array(len);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.length;
  }
  return out;
}
const deflateRaw = (bytes: Uint8Array) => runStream(new CompressionStream("deflate-raw"), bytes);
const inflateRaw = (bytes: Uint8Array) => runStream(new DecompressionStream("deflate-raw"), bytes);

/**
 * Serialize the current configurator state for storing on the item. Only the
 * dynamically-generated tables are kept (the static QTables re-seed from
 * qtable_data.json on load), so the field stays small.
 *
 * Async because DEFLATE (CompressionStream) is async. Falls back to lz-string
 * ("lzc:") when CompressionStream is unavailable.
 */
export async function buildConfigSnapshot(
  state: {
    answers: AnswerState;
    selectedRows: SelectedRowState;
    tables: TableState;
    extraAux?: number;
    // The configurator this configuration was built in (products.json slug).
    // Required: an unstamped snapshot would be indistinguishable from a legacy one
    // and Edit Config would silently open the wrong CPQ.
    product: string;
  },
  qtables: QTables | undefined
): Promise<string> {
  if (!state.product) {
    throw new Error("buildConfigSnapshot requires the product slug");
  }
  const dynTables: TableState = {};
  for (const k of Object.keys(state.tables)) {
    if (!qtables || !(k in qtables)) dynTables[k] = state.tables[k];
  }
  // Drop null/undefined answers. Unselected dropdown options are stored as null,
  // so the map is mostly nulls; they read back identically as "absent" (both are
  // blank to the scripts), and omitting them shrinks the blob.
  const answers: AnswerState = {};
  for (const k of Object.keys(state.answers)) {
    const v = state.answers[k];
    if (v !== null && v !== undefined) answers[k] = v;
  }
  const json = JSON.stringify({
    v: CONFIG_SNAPSHOT_VERSION,
    product: state.product,
    answers,
    selectedRows: state.selectedRows,
    tables: dynTables,
    extraAux: state.extraAux ?? 0,
  });

  if (hasCompression()) {
    const deflated = await deflateRaw(new TextEncoder().encode(json));
    return DEFLATE_PREFIX + bytesToB64url(deflated);
  }
  // Fallback: still transport-safe and readable, just less dense.
  return COMPRESS_PREFIX + LZString.compressToEncodedURIComponent(json);
}

/**
 * Parse any stored snapshot into the current shape. Reads df1: (deflate),
 * lzc:/lz16: (legacy lz-string), and bare JSON. Tolerant: unknown keys ignored,
 * missing keys defaulted. Throws on undecodable/invalid payloads so callers can
 * fall back (e.g. to the item mapping).
 *
 * Async because df1: inflation (DecompressionStream) is async.
 */
export async function parseConfigSnapshot(stored: string): Promise<ConfigSnapshot> {
  let json = stored;
  if (stored.startsWith(DEFLATE_PREFIX)) {
    if (!hasCompression()) {
      throw new Error("Stored configuration needs DEFLATE support, which this environment lacks.");
    }
    const bytes = b64urlToBytes(stored.slice(DEFLATE_PREFIX.length));
    json = new TextDecoder().decode(await inflateRaw(bytes));
  } else if (stored.startsWith(COMPRESS_PREFIX)) {
    const out = LZString.decompressFromEncodedURIComponent(stored.slice(COMPRESS_PREFIX.length));
    if (out == null) throw new Error("Stored configuration could not be decompressed.");
    json = out;
  } else if (stored.startsWith(LEGACY_UTF16_PREFIX)) {
    const out = LZString.decompressFromUTF16(stored.slice(LEGACY_UTF16_PREFIX.length));
    if (out == null) {
      throw new Error("Stored configuration could not be decompressed (legacy lz16 — likely truncated).");
    }
    json = out;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error("Stored configuration is not valid JSON.");
  }
  if (!isPlainObject(parsed)) {
    throw new Error("Stored configuration is empty or malformed.");
  }
  const obj = parsed;
  // Envelope ({ v, answers, ... }) vs a legacy bare answers map.
  const isEnvelope = "answers" in obj || "v" in obj || "tables" in obj || "selectedRows" in obj;
  const rawAnswers = isEnvelope ? obj.answers : obj;
  const v = typeof obj.v === "number" ? obj.v : 0;

  return {
    v,
    // v1 and the legacy bare-answers form carry no product. They were all built in
    // Natural Draft Burners, so that is not a guess — it is the only configurator
    // that existed when they were saved. A non-string or blank value gets the same
    // treatment rather than propagating "" into a data path.
    product:
      typeof obj.product === "string" && obj.product.trim() !== ""
        ? obj.product.trim()
        : LEGACY_PRODUCT_SLUG,
    answers: isPlainObject(rawAnswers) ? (rawAnswers as AnswerState) : {},
    selectedRows: isPlainObject(obj.selectedRows) ? (obj.selectedRows as SelectedRowState) : {},
    tables: isPlainObject(obj.tables) ? (obj.tables as TableState) : {},
    extraAux: typeof obj.extraAux === "number" && obj.extraAux > 0 ? obj.extraAux : 0,
  };
}
